import React, { useCallback, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { useGenericOnError } from '@modernloop/shared/hooks';
import { FlagIcon } from '@modernloop/shared/icons';
import { Stack, Typography, useTheme } from '@mui/material';

import {
  ActorType,
  ResponseStatus,
  ScheduleTaskFlags_TaskFlagFragment,
  SourceType,
  TaskFlagActorType,
  TaskFlagType,
  useTaskFlagMarkResolvedMutation,
} from 'src/generated/mloop-graphql';

import Link from 'src/components/Link';
import { FCWithFragments } from 'src/components/types';

import ActivityLogBase from 'src/entities/ActivityLog/Helpers/ActivityLogBase';
import { EmployeeFragment } from 'src/entities/EmployeePicker/EmployeeGraphQL';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useRefetchScheduleTaskQuery from 'src/hooks/useRefetchScheduleTaskQuery';

import { getTaskFlagLabel } from './Common/ScheduleTaskFlagCompact/getTaskFlagLabel';

const useSxProps = () => {
  const theme = useTheme();

  return useMemo(() => {
    return {
      requestedChangeNote: {
        borderRadius: '12px',
        border: `1px solid ${theme.palette.borderState.default}`,
        padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
        width: 'fit-content',
      },
    };
  }, [theme]);
};

type ScheduleTaskFlagProp = {
  taskFlag: ScheduleTaskFlags_TaskFlagFragment;
};

export type ScheduleTaskFlagPresentation = {
  actionLabelKey: string;
  actionLabelValues?: Record<string, unknown>;
  flagType: TaskFlagType;
  note?: string | null;
  responseStatus?: ResponseStatus;
};

const assertNever = (value: never): never => {
  throw new Error(`Unhandled task flag variant: ${JSON.stringify(value)}`);
};

/** Parses a raw GraphQL TaskFlag union into the complete detail-view presentation model. */
export const getScheduleTaskFlagPresentation = (
  taskFlag: ScheduleTaskFlags_TaskFlagFragment
): ScheduleTaskFlagPresentation => {
  switch (taskFlag.__typename) {
    case 'TaskFlagCandidateDeclined':
      return {
        actionLabelKey: 'hiring-dashboard-tasks:tasks.details.declinedEvent',
        actionLabelValues: { name: taskFlag.declinedCandidateEvent.name },
        flagType: TaskFlagType.CandidateDeclined,
        responseStatus: ResponseStatus.Declined,
      };
    case 'TaskFlagCandidateTentative':
      return {
        actionLabelKey: 'hiring-dashboard-tasks:tasks.details.tentativelyAcceptedEvent',
        actionLabelValues: { name: taskFlag.tentativeCandidateEvent.name },
        flagType: TaskFlagType.CandidateTentative,
        responseStatus: ResponseStatus.Tentative,
      };
    case 'TaskFlagDebriefAttendeeDeactivated':
      return {
        actionLabelKey: 'hiring-dashboard-tasks:tasks.details.deactivatedForDebrief',
        actionLabelValues: { name: taskFlag.deactivatedDebrief.name },
        flagType: TaskFlagType.DebriefAttendeeDeactivated,
      };
    case 'TaskFlagDebriefAttendeeTentative':
      return {
        actionLabelKey: 'hiring-dashboard-tasks:tasks.details.tentativelyAcceptedDebrief',
        actionLabelValues: { name: taskFlag.tentativeDebrief.name },
        flagType: TaskFlagType.DebriefAttendeeTentative,
        responseStatus: ResponseStatus.Tentative,
      };
    case 'TaskFlagInterviewerDebriefDeclined':
      return {
        actionLabelKey: 'hiring-dashboard-tasks:tasks.details.declinedDebrief',
        flagType: TaskFlagType.DebriefDeclined,
      };
    case 'TaskFlagInterviewerDeactivated':
      return {
        actionLabelKey: 'hiring-dashboard-tasks:tasks.details.deactivatedForEvent',
        actionLabelValues: { name: taskFlag.deactivatedInterview.name },
        flagType: TaskFlagType.InterviewerDeactivated,
      };
    case 'TaskFlagInterviewerDeclined':
      return {
        actionLabelKey: 'hiring-dashboard-tasks:tasks.details.declinedEvent',
        actionLabelValues: { name: taskFlag.declinedInterview.name },
        flagType: TaskFlagType.InterviewDeclined,
        responseStatus: ResponseStatus.Declined,
      };
    case 'TaskFlagInterviewerTentative':
      return {
        actionLabelKey: 'hiring-dashboard-tasks:tasks.details.tentativelyAcceptedEvent',
        actionLabelValues: { name: taskFlag.tentativeInterview.name },
        flagType: TaskFlagType.InterviewerTentative,
        responseStatus: ResponseStatus.Tentative,
      };
    case 'TaskFlagRequestedChange':
      return {
        actionLabelKey: 'hiring-dashboard-tasks:tasks.details.requestedAChange',
        flagType: TaskFlagType.RequestChange,
        note: taskFlag.note,
      };
    default:
      return assertNever(taskFlag);
  }
};

const ScheduleTaskFlag = ({ taskFlag }: ScheduleTaskFlagProp) => {
  const { t } = useTranslation();
  const sxProps = useSxProps();
  const [resolving, setResolving] = useState(false);
  const handleError = useGenericOnError();

  const activityFeedRefetch = useActivityFeedRefetch();
  const refetchScheduleTaskQuery = useRefetchScheduleTaskQuery();

  const [taskFlagMarkResolvedMutation] = useTaskFlagMarkResolvedMutation({
    onError: (error) => {
      handleError({ error });
    },
  });

  const presentation = useMemo(() => getScheduleTaskFlagPresentation(taskFlag), [taskFlag]);
  const actionLabel = t(presentation.actionLabelKey, presentation.actionLabelValues);

  const actorType = useMemo(() => {
    if (taskFlag.actorType === TaskFlagActorType.Employee) {
      return ActorType.Employee;
    }
    if (taskFlag.actorType === TaskFlagActorType.Candidate) {
      return ActorType.Candidate;
    }
    return ActorType.System;
  }, [taskFlag.actorType]);

  const handleMarkAsResolved = useCallback(async () => {
    setResolving(true);

    try {
      await taskFlagMarkResolvedMutation({
        variables: {
          input: {
            taskFlagId: taskFlag.id,
          },
        },
        awaitRefetchQueries: true,
      });

      refetchScheduleTaskQuery();
      activityFeedRefetch();
    } finally {
      setResolving(false);
    }
  }, [activityFeedRefetch, refetchScheduleTaskQuery, taskFlag.id, taskFlagMarkResolvedMutation]);

  return (
    <ActivityLogBase
      dataTestId="schedule-task-flag"
      activityLog={{
        __typename: 'ActivityLog',
        createdAt: taskFlag.createdAt,
        id: taskFlag.id,
        activityActorType: actorType,
        actorEmployee: taskFlag.actorEmployee,
        sourceType: SourceType.SourceTypeTask,
      }}
      responseStatus={presentation.responseStatus}
      actionLabel={actionLabel}
    >
      <Stack spacing={presentation.note ? 1 : undefined}>
        {presentation.note && (
          <Typography
            data-testid="schedule-task-flags-request-change-note"
            color="text.secondary"
            style={sxProps.requestedChangeNote}
          >
            {presentation.note}
          </Typography>
        )}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography data-testid="schedule-task-flag-label" variant="body2" color="error.main">
            <FlagIcon color="error" fontSize="small" />
            {getTaskFlagLabel(presentation.flagType, t)}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            ·
          </Typography>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            color={resolving ? 'text.secondary' : 'primary'}
            variant="body2"
            sx={{ pointerEvents: resolving ? 'none' : undefined, textDecoration: 'none' }}
            dataTestId="schedule-task-flags-mark-as-resolved"
            onClick={handleMarkAsResolved}
          >
            {t('hiring-dashboard-tasks:tasks.details.markAsResolved')}
          </Link>
        </Stack>
      </Stack>
    </ActivityLogBase>
  );
};

type ScheduleTaskFlagsFragmentProps = {
  taskFlags: ScheduleTaskFlags_TaskFlagFragment[];
};

// eslint-disable-next-line modernloop/restric-fragments-name.cjs
const ScheduleTaskFlags: FCWithFragments<ScheduleTaskFlagsFragmentProps> = ({ taskFlags }) => {
  const { t } = useTranslation();
  const flags = useMemo(() => {
    return taskFlags.filter((flag) => !flag.resolvedAt);
  }, [taskFlags]);

  if (!flags.length) {
    return (
      <Typography data-testid="schedule-task-flags-none" color="text.disabled" variant="body2">
        {t('hiring-dashboard-tasks:tasks.details.none')}
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {flags.map((taskFlag) => {
        return <ScheduleTaskFlag key={taskFlag.id} taskFlag={taskFlag} />;
      })}
    </Stack>
  );
};

ScheduleTaskFlags.fragments = {
  taskFlags: gql`
    ${EmployeeFragment}
    fragment ScheduleTaskFlags_taskFlag on TaskFlag {
      __typename
      id
      createdAt
      flagType
      actorType
      actorEmployee {
        ...Employee
      }
      resolvedAt
      resolvedBy {
        ...Employee
      }

      ... on TaskFlagCandidateDeclined {
        declinedCandidateEvent {
          id
          name
        }
      }

      ... on TaskFlagInterviewerDeclined {
        declinedInterview {
          id
          name
        }
      }

      ... on TaskFlagRequestedChange {
        note
      }

      ... on TaskFlagInterviewerDebriefDeclined {
        declinedDebrief {
          id
          name
        }
      }

      ... on TaskFlagInterviewerDeactivated {
        deactivatedInterview {
          id
          name
        }
      }

      ... on TaskFlagInterviewerTentative {
        tentativeInterview {
          id
          name
        }
      }

      ... on TaskFlagDebriefAttendeeTentative {
        tentativeDebrief {
          id
          name
        }
      }

      ... on TaskFlagDebriefAttendeeDeactivated {
        deactivatedDebrief {
          id
          name
        }
      }

      ... on TaskFlagCandidateTentative {
        tentativeCandidateEvent {
          id
          name
        }
      }
    }
  `,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskFlagMarkResolvedMutation = gql`
  ${EmployeeFragment}
  ${ScheduleTaskFlags.fragments.taskFlags}
  mutation TaskFlagMarkResolved($input: TaskFlagMarkResolvedInput!) {
    taskFlagMarkResolved(input: $input) {
      taskFlag {
        ...ScheduleTaskFlags_taskFlag
      }
    }
  }
`;

export default ScheduleTaskFlags;
